import { api } from './api'
import { db, dirtyTours, getMeta, setMeta } from './db'
import type { Tour, Sport, TourKind, TourData } from './models'
import { simplify } from './geo'

const MAX_SYNC_POINTS = 3000

/** Shrink oversized recorded tracks before pushing (server caps row size). */
function syncPayload(t: Tour) {
  let data = t.data
  if (data.coords.length > MAX_SYNC_POINTS) {
    // drop per-point times when simplifying — index alignment would break
    data = { ...data, coords: simplify(data.coords, 5), times: undefined, gaps: undefined }
  }
  return { id: t.id, name: t.name, sport: t.sport, kind: t.kind, data, updatedAt: t.updatedAt, deleted: !!t.deleted }
}

export interface SyncResult {
  pushed: number
  pulled: number
}

/**
 * Two-way sync, last-write-wins on updatedAt.
 * Push local dirty tours, then pull everything newer than the stored cursor.
 */
export async function syncTours(): Promise<SyncResult> {
  const d = await db()

  const dirty = await dirtyTours()
  let pushed = 0
  for (let i = 0; i < dirty.length; i += 25) {
    const batch = dirty.slice(i, i + 25)
    const res = await api.pushTours(batch.map(syncPayload))
    const ok = new Set(res.accepted)
    const tx = d.transaction('tours', 'readwrite')
    for (const t of batch) {
      if (ok.has(t.id)) {
        tx.store.put({ ...t, synced: true })
        pushed++
      }
    }
    await tx.done
  }

  const since = Number((await getMeta('sync.cursor')) ?? 0)
  const res = await api.pullTours(since)
  let pulled = 0
  const tx = d.transaction('tours', 'readwrite')
  for (const remote of res.tours) {
    const local = await tx.store.get(remote.id)
    if (local && local.updatedAt >= remote.updatedAt) continue
    pulled++
    if (remote.deleted) {
      if (local) tx.store.put({ ...local, deleted: true, synced: true, updatedAt: remote.updatedAt })
      continue
    }
    tx.store.put({
      id: remote.id,
      name: remote.name,
      sport: remote.sport as Sport,
      kind: remote.kind as TourKind,
      data: remote.data as TourData,
      createdAt: local?.createdAt ?? remote.updatedAt,
      updatedAt: remote.updatedAt,
      synced: true,
    })
  }
  await tx.done
  await setMeta('sync.cursor', res.now)
  return { pushed, pulled }
}
